import React from "react";
import { Package } from "lucide-react";
import { Modal, Badge } from "../../../components/ui";

const InfoCell = ({ label, value, fullWidth = false }) => {
  // Robust sanitization to prevent "Objects are not valid as a React child" errors
  const getSafeDisplayValue = (val) => {
    if (val === undefined || val === null || val === "") return "—";
    
    if (typeof val === 'object') {
      if (val instanceof Date) return val.toLocaleDateString();
      
      const candidate = val.subCategoryName || val.categoryName || val.brandName || val.unitName || val.storeName || val.registeredTypeName || val.name || val.title || val.label;
      
      // TRULY Robust: Only return if it's a primitive
      if (typeof candidate === 'string') return candidate;
      if (typeof candidate === 'number') return String(candidate);
      if (typeof candidate === 'boolean') return String(candidate);
      
      return "—";
    }
    
    return String(val);
  };

  const displayValue = getSafeDisplayValue(value);
  
  return (
    <div className={`bg-[#f8fafc] rounded-lg p-3.5 transition-all hover:bg-[#f1f5f9] border border-transparent hover:border-slate-100 group ${fullWidth ? "col-span-2" : ""}`}>
      <div className="text-slate-700 font-bold text-[12px] tracking-wide mb-1">
        {label}
      </div>
      <div className="text-[12px] font-medium text-slate-800 break-words leading-snug">
        {displayValue}
      </div>
    </div>
  );
};

export const InventoryViewModal = ({ isOpen, onClose, activeTab, item }) => {
  if (!item) return null;

  const getSections = () => {
    if (activeTab === "vendor") {
      return [
        {
          items: [
            { label: "Mobile", value: item.mobile },
            { label: "Email", value: item.email },
            {
              label: "Address Line 1",
              value: item.addressLine1,
              fullWidth: true,
            },
            {
              label: "Address Line 2",
              value: item.addressLine2,
              fullWidth: true,
            },
            { label: "City", value: item.city },
            { label: "State", value: item.state },
            { label: "Pincode", value: item.pincode },
            { 
              label: "Created Date", 
              value: item.createdAt || item.createdDate || item.created 
                ? new Date(item.createdAt || item.createdDate || item.created).toLocaleDateString() 
                : "—" 
            },
          ],
        },
        {
          items: [
            { label: "GST Type", value: item.gstType },
            { label: "GST No", value: item.gstNo },
            { label: "Bank Name", value: item.bankName },
            { label: "Branch", value: item.branchName },
            {
              label: "Account No",
              value: item.accountNumber,
            },
            { label: "IFSC Code", value: item.ifscCode },
            { label: "Account Holder", value: item.accountHolderName },
            { label: "Description", value: item.description, fullWidth: true },
          ],
        },
      ];
    }

    if (activeTab === "item") {
      return [
        {
          items: [
            // { label: "Item Name", value: item.itemName || item.name || item.title },
            // { label: "Item Code", value: item.itemCode || item.code },
            { label: "Item ID", value: item.itemId || "—" },
            { label: "Item Type", value: item.itemType || "Product" },
            { label: "Category", value: item.category?.categoryName || item.category?.title || "—" },
            { label: "Brand", value: item.brand?.brandName || item.brand?.title || "—" },
            { label: "Units", value: item.units?.unitName || item.units?.title || "—" },
            { label: "HSN/SAC Code", value: item.hsnSacCode },
            { label: "Barcode", value: item.barcode || "—" },
            { label: "Serial Managed", value: item.isUnique ? "YES" : "NO" },
            ...(item.isUnique ? [{ label: "Unique Identifier Name", value: item.uniqueName || "N/A (Serial No)" }] : []),
            { label: "Batch Managed", value: item.hasBatch || item.isBatchExpiry ? "YES" : "NO" },
            { label: "Status", value: item.status },
            { 
              label: "Created Date", 
              value: item.createdDate || item.createdAt || item.created 
                ? new Date(item.createdDate || item.createdAt || item.created).toLocaleDateString() 
                : "—" 
            },
            {
              label: "Description",
              value: item.description || item.itemDescription,
              fullWidth: true,
            },
          ],
        },
      ];
    }

    if (activeTab === "brand") {
      return [
        {
          items: [
            { label: "Brand Name", value: item.brandName || item.title || item.name },
            { label: "Brand Code", value: item.brandCode || item.code },
            { label: "Brand ID", value: item.brandId },
            { label: "Status", value: item.status },
            { 
              label: "Created Date", 
              value: item.createdAt || item.createdDate || item.created 
                ? new Date(item.createdAt || item.createdDate || item.created).toLocaleDateString() 
                : "—" 
            },
            { label: "Description", value: item.description, fullWidth: true },
          ],
        },
      ];
    }

    if (activeTab === "category") {
      return [
        {
          items: [
            { label: "Category Name", value: item.categoryName || item.title || item.name },
            { label: "Category Code", value: item.categoryCode || item.code },
            { label: "Category ID", value: item.categoryId },
            { label: "Status", value: item.status },
            { 
              label: "Created Date", 
              value: item.createdAt || item.createdDate || item.created 
                ? new Date(item.createdAt || item.createdDate || item.created).toLocaleDateString() 
                : "—" 
            },
            { label: "Description", value: item.description, fullWidth: true },
          ],
        },
      ];
    }

    if (activeTab === "subCategory") {
      return [
        {
          items: [
            { label: "Sub Category Name", value: item.subCategoryName || item.title || item.name },
            { label: "Sub Category Code", value: item.subCategoryCode || item.code },
            { label: "Sub Category ID", value: item.subCategoryId },
            { label: "Status", value: item.status },
            { 
              label: "Created Date", 
              value: item.createdAt || item.createdDate || item.created 
                ? new Date(item.createdAt || item.createdDate || item.created).toLocaleDateString() 
                : "—" 
            },
            { label: "Description", value: item.description, fullWidth: true },
          ],
        },
      ];
    }

    if (activeTab === "stores") {
      return [
        {
          items: [
            { label: "Store ID", value: item.storeId },
            { label: "Mobile", value: item.mobile },
            { label: "Status", value: item.status },
            { label: "Description", value: item.description, fullWidth: true },
          ],
        },
        {
          items: [
            { label: "Address Line 1", value: item.addressLine1, fullWidth: true },
            { label: "Address Line 2", value: item.addressLine2, fullWidth: true },
            { label: "City", value: item.city },
            { label: "State", value: item.state },
            { label: "Pincode", value: item.pincode },
            { 
              label: "Created Date", 
              value: item.createdAt || item.createdDate || item.created 
                ? new Date(item.createdAt || item.createdDate || item.created).toLocaleDateString() 
                : "—" 
            },
          ],
        },
      ];
    }

    if (activeTab === "unit") {
      return [
        {
          items: [
            { label: "Unit Name", value: item.unitName || item.title || item.name },
            { label: "Unit Code", value: item.unitCode || item.code },
            { label: "Unit ID", value: item.unitId },
            { label: "Status", value: item.status },
            { 
              label: "Created Date", 
              value: item.createdAt || item.createdDate || item.created 
                ? new Date(item.createdAt || item.createdDate || item.created).toLocaleDateString() 
                : "—" 
            },
            { label: "Description", value: item.description, fullWidth: true },
          ],
        },
      ];
    }

    if (activeTab === "registeredType") {
      return [
        {
          items: [
            { label: "Type Name", value: item.registeredTypeName || item.title || item.name },
            { label: "Type Code", value: item.registeredTypeCode || item.code },
            { label: "Type ID", value: item.registeredTypeId },
            { label: "Status", value: item.status },
            { 
              label: "Created Date", 
              value: item.createdAt || item.createdDate || item.created 
                ? new Date(item.createdAt || item.createdDate || item.created).toLocaleDateString() 
                : "—" 
            },
            { label: "Description", value: item.description, fullWidth: true },
          ],
        },
      ];
    }

    return [
      {
        title: "Details",
        items: [
          { label: "Title", value: item.title },
          { label: "Code", value: item.code },
          { label: "Status", value: item.status },
          { label: "Description", value: item.description, fullWidth: true },
          ...(activeTab === "stores"
            ? [
                {
                  label: "Address",
                  value: item.address,
                  fullWidth: true,
                },
                { label: "Mobile", value: item.mobile },
              ]
            : []),
        ],
      },
    ];
  };

  const renderHeader = () => {
    let displayName = "";
    let displayCode = "";
    let status = "";

    if (activeTab === "vendor") {
      displayName = item.vendorName || item.name || item.title;
      displayCode = item.vendorCode || item.code;
      status = item.status;
    } else if (activeTab === "item") {
      displayName = item.itemName || item.name || item.title;
      displayCode = item.itemCode || item.code;
      status = item.status;
    } else if (activeTab === "brand") {
      displayName = item.brandName || item.name || item.title;
      displayCode = item.brandCode || item.code;
      status = item.status;
    } else if (activeTab === "category") {
      displayName = item.categoryName || item.name || item.title;
      displayCode = item.categoryCode || item.code;
      status = item.status;
    } else if (activeTab === "subCategory") {
      displayName = item.subCategoryName || item.name || item.title;
      displayCode = item.subCategoryCode || item.code;
      status = item.status;
    } else if (activeTab === "stores") {
      displayName = item.storeName || item.name || item.title;
      displayCode = item.storeCode || item.code;
      status = item.status;
    } else if (activeTab === "unit") {
      displayName = item.unitName || item.name || item.title;
      displayCode = item.unitCode || item.code;
      status = item.status;
    } else if (activeTab === "registeredType") {
      displayName = item.registeredTypeName || item.name || item.title;
      displayCode = item.registeredTypeCode || item.code;
      status = item.status;
    } else {
      displayName = item.title || item.name || item.itemName;
      displayCode = item.code || item.itemCode;
      status = item.status;
    }

    const stringify = (val) => {
      if (typeof val === 'string') return val;
      if (typeof val === 'number') return String(val);
      if (val && typeof val === 'object') {
        const candidate = val.subCategoryName || val.categoryName || val.brandName || val.unitName || val.storeName || val.registeredTypeName || val.name || val.title;
        if (typeof candidate === 'string') return candidate;
        if (typeof candidate === 'number') return String(candidate);
      }
      return "";
    };

    const safeName = stringify(displayName);
    const safeCode = stringify(displayCode);
    const isActive = status === "ACTIVE" || status === "active" || item.active !== false;

    return (
      <div className="flex items-center gap-5 pb-5 border-b border-slate-100/60 mb-6">
        <div className="w-16 h-16 rounded-full bg-slate-50 border border-slate-100 flex items-center justify-center shadow-sm">
          <Package size={28} className="text-brand-blue" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-1.5">
            <h2 className="text-[20px] font-black text-slate-800 tracking-tight leading-none uppercase">
              {safeName}
            </h2>
            {safeCode && (
              <Badge 
                variant="bg-blue-50 text-brand-blue border-blue-100" 
                label={safeCode.toUpperCase()} 
                className="uppercase tracking-widest text-[9px] px-3 py-1 rounded-md font-black" 
              />
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${isActive ? 'bg-emerald-500' : 'bg-slate-300'}`} />
            <span className={`text-[13px] font-bold ${isActive ? 'text-emerald-600' : 'text-slate-400'}`}>
              {isActive ? "Active" : "Inactive"}
            </span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`View ${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Details`} size="lg">
      <div className="p-1">
        {renderHeader()}
        <div className="space-y-6">
          {getSections().map((section, idx) => (
            <div key={idx} className="space-y-4">
              {section.title && (
                <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                  <span className="w-1 h-4 bg-brand-blue rounded-full"></span>
                  {section.title}
                </h3>
              )}
              <div className="grid grid-cols-2 gap-3.5">
                {section.items.map((field, fIdx) => (
                  <InfoCell key={fIdx} {...field} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
};
